import React, { useState, useEffect, useRef } from "react";
import SideBar from "../components/SideBar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/EditCustomerProfile.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import { advertisingAgentAPI } from "../services/advertisingAgentService";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { clampAdminPhoneString, phoneErrorSuperAdmin } from "../utils/phone";
import { isValidPostalCode } from "../utils/postalCodeValidator";


const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AdvertisingAgentEdit() {
  const [step, setStep] = useState(1);
  const location = useLocation();
  const agent = location.state?.agent || {};
  const application = location.state?.application;
  const navigate = useNavigate();
  const lastSelectedPhoneCountryRef = useRef(null);

  const getPhoneIso2 = (phoneCountry) => {
    if (!phoneCountry) return undefined;
    return phoneCountry.countryCode || phoneCountry.iso2 || undefined;
  };

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State initialization with actual API data structure
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressLine3, setAddressLine3] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Admin Area States
  const [assignedAreas, setAssignedAreas] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [counties, setCounties] = useState([]);
  const [areaFilters, setAreaFilters] = useState({
    county_id: "",
    search: "",
  });
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Focus Countries States
  const [assignedFocusCountries, setAssignedFocusCountries] = useState([]);
  const [availableFocusCountries, setAvailableFocusCountries] = useState([]);
  const [selectedFocusCountries, setSelectedFocusCountries] = useState([]);
  const [focusCountryFilters, setFocusCountryFilters] = useState({
    region: "",
    search: "",
  });
  const [focusTypeOptions] = useState([
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "target", label: "Target" },
  ]);
  const [replaceExistingFocus, setReplaceExistingFocus] = useState(false);
  const [loadingFocusCountries, setLoadingFocusCountries] = useState(false);
  const [focusCountryStats, setFocusCountryStats] = useState({
    total: 0,
    primary: 0,
    secondary: 0,
    target: 0,
  });

  // Validation states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [line1Error, setLine1Error] = useState("");
  const [cityError, setCityError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState("");

  // Store original values for comparison
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [originalPostalCode, setOriginalPostalCode] = useState("");

  // Check if agent is Senior Advertising Agent
  const isSeniorAgent =
    agent.agent_type === "senior" || application?.agent_type === "senior";
  const isMiniAdvertisingAgent = !isSeniorAgent;
  const isAdvertisingAgentForFocus = isSeniorAgent || isMiniAdvertisingAgent;
  const focusCountriesStep = isSeniorAgent ? 5 : 4;
  const maxWizardStep = isSeniorAgent ? 5 : 4;

  // Get agent ID from multiple possible sources
  const getAgentId = () => {
    return agent.agent_true_id || application?.agent_id || agent.application_id;
  };

  // Snackbar close handler
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Show success message
  const showSuccess = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "success",
    });
  };

  // Show error message
  const showError = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "error",
    });
  };

  // Show info message
  const showInfo = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "info",
    });
  };

  // Show warning message
  const showWarning = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "warning",
    });
  };

  // Initialize form with agent data
  useEffect(() => {
    if (agent) {
      const agentEmail = agent.email || "";
      const agentPhone = agent.phone_number || agent.mobile || "";

      setFirstName(agent.first_name || agent.firstName || "");
      setLastName(agent.last_name || agent.surname || "");
      setEmail(agentEmail);
      setPhoneNumber(agentPhone);
      setAddressLine1(agent.address_line1 || agent.address?.line1 || "");
      setAddressLine2(agent.address_line2 || agent.address?.line2 || "");
      setAddressLine3(agent.address_line3 || agent.address?.line3 || "");
      setCity(agent.city || agent.address?.city || "");
      setPostalCode(
        agent.postcode || agent.address?.postcode || agent.postalCode || "",
      );
      setRegion(agent.region || agent.address?.county || "");
      setCountry(agent.country || agent.address?.country || "");
      setNote(agent.status_note || agent.notes || agent.note || "");

      // Store original values
      setOriginalEmail(agentEmail);
      setOriginalPhone(agentPhone);
      setOriginalPostalCode(
        agent.postcode || agent.address?.postcode || agent.postalCode || "",
      );

      // Set status with proper formatting
      const currentStatus = agent.current_status || agent.status;
      if (currentStatus) {
        setStatus({
          value: currentStatus,
          label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1),
        });
      }
    }
  }, [agent, application]);

  // Load data when steps become active
  useEffect(() => {
    if (step === 4 && isSeniorAgent) {
      fetchCounties();
      fetchAssignedAreas();
      fetchAvailableAreas();
    } else if (
      focusCountriesStep != null &&
      step === focusCountriesStep &&
      isAdvertisingAgentForFocus
    ) {
      fetchAssignedFocusCountries();
      fetchAvailableFocusCountries();
      fetchFocusCountryStatistics();
    }
  }, [step]);

  // Debounce search filter for admin areas
  useEffect(() => {
    if (step === 4 && isSeniorAgent) {
      const timer = setTimeout(() => {
        fetchAvailableAreas();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [areaFilters, step]);

  // Debounce search filter for focus countries
  useEffect(() => {
    if (
      focusCountriesStep != null &&
      step === focusCountriesStep &&
      isAdvertisingAgentForFocus
    ) {
      const timer = setTimeout(() => {
        fetchAvailableFocusCountries();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [focusCountryFilters, step]);

  // Fetch counties for filter
  const fetchCounties = async () => {
    try {
      const response = await advertisingAgentAPI.getCounties();
      if (response.success) {
        setCounties(
          response.data.map((county) => ({
            value: county.county_id,
            label: county.county_name,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching counties:", error);
    }
  };

  // Fetch assigned admin areas
  const fetchAssignedAreas = async () => {
    try {
      const response =
        await advertisingAgentAPI.getAgentAdminAreas(getAgentId());
      if (response.success) {
        setAssignedAreas(response.data.assigned_admin_areas || []);
      }
    } catch (error) {
      console.error("Error fetching assigned areas:", error);
      showError("Failed to load assigned areas");
    }
  };

  // Fetch available admin areas
  const fetchAvailableAreas = async () => {
    setLoadingAreas(true);
    try {
      const params = {
        ...areaFilters,
        include_assignments: true,
        agent_id: getAgentId(),
        per_page: 100,
      };

      if (areaFilters.county_id === "") delete params.county_id;
      if (areaFilters.search === "") delete params.search;

      const response = await advertisingAgentAPI.getAvailableAdminAreas(params);
      if (response.success) {
        setAvailableAreas(response.data.admin_areas || []);
      }
    } catch (error) {
      console.error("Error fetching available areas:", error);
      showError("Failed to load available areas");
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch assigned focus countries
  const fetchAssignedFocusCountries = async () => {
    try {
      const response =
        await advertisingAgentAPI.getAgentFocusCountries(getAgentId());
      if (response.success) {
        setAssignedFocusCountries(response.data.focus_countries || []);

        // Update statistics
        if (response.data.statistics) {
          setFocusCountryStats(response.data.statistics);
        }
      }
    } catch (error) {
      console.error("Error fetching assigned focus countries:", error);
      showError("Failed to load assigned focus countries");
    }
  };

  // Fetch available focus countries
  const fetchAvailableFocusCountries = async () => {
    setLoadingFocusCountries(true);
    try {
      const response = await advertisingAgentAPI.getAvailableFocusCountries(
        getAgentId(),
        focusCountryFilters,
      );

      if (response.success) {
        // Deduplicate countries by name (if API returns duplicates)
        const countries = response.data.available_countries || [];

        // Create a Map to deduplicate by country name
        const uniqueCountriesMap = new Map();

        countries.forEach((country) => {
          // Use country name as key (case-insensitive)
          const key = country.name.toLowerCase().trim();

          // Only add if not already in map
          if (!uniqueCountriesMap.has(key)) {
            uniqueCountriesMap.set(key, country);
          }
        });

        // Convert back to array
        const uniqueCountries = Array.from(uniqueCountriesMap.values());

        // Sort alphabetically by name
        uniqueCountries.sort((a, b) => a.name.localeCompare(b.name));

        setAvailableFocusCountries(uniqueCountries);
      }
    } catch (error) {
      console.error("Error fetching available focus countries:", error);
      showError("Failed to load available focus countries");
    } finally {
      setLoadingFocusCountries(false);
    }
  };

  // Fetch focus country statistics
  const fetchFocusCountryStatistics = async () => {
    try {
      const response = await advertisingAgentAPI.getFocusCountriesStatistics();
      if (response.success) {
        // You can use this data for insights if needed
        console.log("Focus country statistics:", response.data);
      }
    } catch (error) {
      console.error("Error fetching focus country statistics:", error);
    }
  };

  // Handle removing a single focus country
  const handleRemoveFocusCountry = async (countryId) => {
    if (
      !window.confirm("Are you sure you want to remove this focus country?")
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await advertisingAgentAPI.removeFocusCountries(
        getAgentId(),
        [countryId],
      );

      if (response.success) {
        await fetchAssignedFocusCountries();
        await fetchAvailableFocusCountries();
        showSuccess("Focus country removed successfully");
      }
    } catch (error) {
      console.error("Error removing focus country:", error);
      showError(error.message || "Failed to remove focus country");
    } finally {
      setLoading(false);
    }
  };

  // Handle removing all focus countries
  const handleRemoveAllFocusCountries = async () => {
    if (
      !window.confirm(
        "Are you sure you want to remove ALL assigned focus countries?",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const countryIds = assignedFocusCountries.map(
        (country) => country.country_id,
      );

      if (countryIds.length === 0) {
        showWarning("No focus countries to remove");
        return;
      }

      const response = await advertisingAgentAPI.removeFocusCountries(
        getAgentId(),
        countryIds,
      );

      if (response.success) {
        await fetchAssignedFocusCountries();
        await fetchAvailableFocusCountries();
        setSelectedFocusCountries([]);
        showSuccess("All focus countries removed successfully");
      }
    } catch (error) {
      console.error("Error removing all focus countries:", error);
      showError(error.message || "Failed to remove all focus countries");
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk remove selected focus countries
  const handleBulkRemoveSelectedFocus = async () => {
    if (selectedFocusCountries.length === 0) {
      showWarning("Please select focus countries to remove");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to remove ${selectedFocusCountries.length} selected focus country(s)?`,
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await advertisingAgentAPI.removeFocusCountries(
        getAgentId(),
        selectedFocusCountries.map((country) => country.country_id),
      );

      if (response.success) {
        await fetchAssignedFocusCountries();
        await fetchAvailableFocusCountries();
        setSelectedFocusCountries([]);
        showSuccess(
          `${selectedFocusCountries.length} focus country(s) removed successfully`,
        );
      }
    } catch (error) {
      console.error("Error removing selected focus countries:", error);
      showError(error.message || "Failed to remove selected focus countries");
    } finally {
      setLoading(false);
    }
  };

  // Handle update focus type
  const handleUpdateFocusType = async (
    countryId,
    newFocusType,
    notes = null,
  ) => {
    setLoading(true);
    try {
      const updateData = { focus_type: newFocusType };
      if (notes !== null) {
        updateData.notes = notes;
      }

      const response = await advertisingAgentAPI.updateFocusCountry(
        getAgentId(),
        countryId,
        updateData,
      );

      if (response.success) {
        await fetchAssignedFocusCountries();
        showSuccess("Focus country updated successfully");
      }
    } catch (error) {
      console.error("Error updating focus country:", error);
      showError(error.message || "Failed to update focus country");
    } finally {
      setLoading(false);
    }
  };

  // Handle assign focus countries - automatically set as primary
  const handleAssignFocusCountries = async () => {
    if (selectedFocusCountries.length === 0) {
      showWarning("Please select at least one focus country");
      return false;
    }

    setLoading(true);
    try {
      const focusCountriesData = selectedFocusCountries.map((country) => ({
        country_id: country.country_id || country.id,
        focus_type: "primary", // Always set as primary
        notes: null, // No notes by default
      }));

      const response = await advertisingAgentAPI.assignFocusCountries(
        getAgentId(),
        focusCountriesData,
        replaceExistingFocus,
      );

      if (response.success) {
        await fetchAssignedFocusCountries();
        await fetchAvailableFocusCountries();
        setSelectedFocusCountries([]);
        showSuccess(
          `${selectedFocusCountries.length} focus country(s) assigned successfully as primary`,
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error assigning focus countries:", error);
      showError(error.message || "Failed to assign focus countries");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Toggle focus country selection
  const toggleFocusCountrySelection = (country) => {
    if (
      selectedFocusCountries.some((c) => c.country_id === country.country_id)
    ) {
      setSelectedFocusCountries(
        selectedFocusCountries.filter(
          (c) => c.country_id !== country.country_id,
        ),
      );
    } else {
      setSelectedFocusCountries([...selectedFocusCountries, country]);
    }
  };

  // Select all visible focus countries
  const selectAllVisibleFocus = () => {
    setSelectedFocusCountries(availableFocusCountries);
    if (availableFocusCountries.length > 0) {
      showInfo(
        `${availableFocusCountries.length} focus countries selected (will be assigned as primary)`,
      );
    }
  };

  // Clear focus country selections
  const clearFocusSelections = () => {
    setSelectedFocusCountries([]);
    showInfo("Selections cleared");
  };

  // Toggle area selection
  const toggleAreaSelection = (area) => {
    if (selectedAreas.some((a) => a.admin_area_id === area.admin_area_id)) {
      setSelectedAreas(
        selectedAreas.filter((a) => a.admin_area_id !== area.admin_area_id),
      );
    } else {
      setSelectedAreas([...selectedAreas, area]);
    }
  };

  // Select all visible areas
  const selectAllVisible = () => {
    const unassignedAreas = availableAreas.filter((area) => !area.is_assigned);
    setSelectedAreas(unassignedAreas);
    if (unassignedAreas.length > 0) {
      showInfo(`${unassignedAreas.length} areas selected`);
    }
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedAreas([]);
    showInfo("Selections cleared");
  };

  // Handle assign areas
  const handleAssignAreas = async () => {
    if (selectedAreas.length === 0) {
      showWarning("Please select at least one admin area");
      return false;
    }

    setLoading(true);
    try {
      const response = await advertisingAgentAPI.assignAdminAreas(
        getAgentId(),
        selectedAreas.map((area) => area.admin_area_id),
        replaceExisting,
      );

      if (response.success) {
        await fetchAssignedAreas();
        await fetchAvailableAreas();
        setSelectedAreas([]);
        showSuccess(`${selectedAreas.length} area(s) assigned successfully`);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error assigning areas:", error);
      showError(error.message || "Failed to assign admin areas");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle removing a single area
  const handleRemoveArea = async (adminAreaId) => {
    if (!window.confirm("Are you sure you want to remove this admin area?")) {
      return;
    }

    setLoading(true);
    try {
      const response = await advertisingAgentAPI.removeAdminAreas(
        getAgentId(),
        [adminAreaId],
      );

      if (response.success) {
        await fetchAssignedAreas();
        await fetchAvailableAreas();
        showSuccess("Admin area removed successfully");
      }
    } catch (error) {
      console.error("Error removing area:", error);
      showError(error.message || "Failed to remove admin area");
    } finally {
      setLoading(false);
    }
  };

  // Handle removing all areas
  const handleRemoveAllAreas = async () => {
    if (
      !window.confirm(
        "Are you sure you want to remove ALL assigned admin areas?",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const areaIds = assignedAreas.map((area) => area.admin_area_id);

      if (areaIds.length === 0) {
        showWarning("No areas to remove");
        return;
      }

      const response = await advertisingAgentAPI.removeAdminAreas(
        getAgentId(),
        areaIds,
      );

      if (response.success) {
        await fetchAssignedAreas();
        await fetchAvailableAreas();
        showSuccess("All admin areas removed successfully");
      }
    } catch (error) {
      console.error("Error removing all areas:", error);
      showError(error.message || "Failed to remove all admin areas");
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk remove selected areas
  const handleBulkRemoveSelected = async () => {
    if (selectedAreas.length === 0) {
      showWarning("Please select areas to remove");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to remove ${selectedAreas.length} selected area(s)?`,
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await advertisingAgentAPI.removeAdminAreas(
        getAgentId(),
        selectedAreas.map((area) => area.admin_area_id),
      );

      if (response.success) {
        await fetchAssignedAreas();
        await fetchAvailableAreas();
        setSelectedAreas([]);
        showSuccess(`${selectedAreas.length} area(s) removed successfully`);
      }
    } catch (error) {
      console.error("Error removing selected areas:", error);
      showError(error.message || "Failed to remove selected areas");
    } finally {
      setLoading(false);
    }
  };

  // Check email availability only (removed phone checking)
  const checkEmailAvailability = async () => {
    let emailValid = true;

    // Check email format (only if changed)
    if (email !== originalEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        emailValid = false;
      }
    }

    // If email not changed, no need to check
    if (email === originalEmail) {
      return { emailAvailable: true };
    }

    // Set email to checking state
    setEmailChecking(true);
    setEmailAvailable(null);
    setEmailCheckMessage("Checking email availability...");

    try {
      if (email !== originalEmail && emailValid) {
        const emailResult = await advertisingAgentAPI.checkEmailAvailability(email);
        setEmailAvailable(emailResult.available);
        setEmailCheckMessage(emailResult.message);
        return { emailAvailable: emailResult.available };
      }
      
      return { emailAvailable: true };
    } catch (error) {
      console.error("Email availability check error:", error);
      setEmailAvailable(null);
      setEmailCheckMessage("Error checking email availability");
      showError("Failed to check email availability");
      return { emailAvailable: false };
    } finally {
      setEmailChecking(false);
    }
  };

  const nextStep = async () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;

      const availability = await checkEmailAvailability();

      if (email !== originalEmail) {
        if (availability.emailAvailable === false) {
          setEmailError("Email is already registered");
          return;
        }
        if (availability.emailAvailable === null) {
          setEmailError(
            "Unable to verify email availability. Please try again.",
          );
          return;
        }
      }

      if (email === originalEmail || availability.emailAvailable === true) {
        setStep((prev) => Math.min(prev + 1, maxWizardStep));
      }
    } else if (step === 2) {
      const isValid = validateAddressDetails();
      if (!isValid) return;
      setStep((prev) => Math.min(prev + 1, maxWizardStep));
    } else if (step === 3) {
      if (isSeniorAgent) {
        setStep(4);
      } else if (isMiniAdvertisingAgent) {
        setStep(4);
      } else {
        await handleUpdate();
      }
    } else if (step === 4) {
      if (isSeniorAgent) {
        // Check if there are any changes to admin areas
        const hasRemovedAreas = selectedAreas.length > 0; // Areas selected for removal
        const hasAddedAreas = selectedAreas.length > 0; // Areas selected for addition

        if (hasRemovedAreas || hasAddedAreas) {
          const areasAssigned = await handleAssignAreas();
          if (areasAssigned) {
            setStep(5);
          }
        } else {
          // No changes to admin areas, just move to next step
          setStep(5);
        }
      } else if (isMiniAdvertisingAgent) {
        const hasRemovedFocus = selectedFocusCountries.length > 0;
        const hasAddedFocus = selectedFocusCountries.length > 0;

        if (hasRemovedFocus || hasAddedFocus) {
          const focusAssigned = await handleAssignFocusCountries();
          if (focusAssigned) {
            await handleUpdate();
          }
        } else {
          await handleUpdate();
        }
      }
    } else if (step === 5 && isSeniorAgent) {
      // Check if there are any changes to focus countries
      const hasRemovedFocus = selectedFocusCountries.length > 0; // Countries selected for removal
      const hasAddedFocus = selectedFocusCountries.length > 0; // Countries selected for addition

      if (hasRemovedFocus || hasAddedFocus) {
        const focusAssigned = await handleAssignFocusCountries();
        if (focusAssigned) {
          await handleUpdate();
        }
      } else {
        // No changes to focus countries, just update
        await handleUpdate();
      }
    }
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleStepClick = (stepNumber) => {
    if (stepNumber === step) return;
    setStep(stepNumber);
  };

  const steps = [
    { label: "Basic details", sub: "Enter your basic details" },
    { label: "Address", sub: "Enter your address details" },
    { label: "Status", sub: "Update the status" },
  ];

  // Add Admin Areas and Focus Countries steps for senior; Focus only for mini advertising agents
  if (isSeniorAgent) {
    steps.push({ label: "Admin Areas", sub: "Manage assigned areas" });
    steps.push({ label: "Focus Countries", sub: "Manage focus countries" });
  } else if (isMiniAdvertisingAgent) {
    steps.push({ label: "Focus Countries", sub: "Manage focus countries" });
  }

  const regionOptions = {
    // Africa
    Afghanistan: ["Asia"],
    Albania: ["Europe"],
    Algeria: ["Africa"],
    Andorra: ["Europe"],
    Angola: ["Africa"],
    Argentina: ["South America"],
    Armenia: ["Asia"],
    Australia: ["Oceania"],
    Austria: ["Europe"],
    Azerbaijan: ["Asia"],
    Bahamas: ["North America"],
    Bahrain: ["Asia"],
    Bangladesh: ["Asia"],
    Barbados: ["North America"],
    Belarus: ["Europe"],
    Belgium: ["Europe"],
    Belize: ["North America"],
    Benin: ["Africa"],
    Bhutan: ["Asia"],
    Bolivia: ["South America"],
    "Bosnia and Herzegovina": ["Europe"],
    Botswana: ["Africa"],
    Brazil: ["South America"],
    Brunei: ["Asia"],
    Bulgaria: ["Europe"],
    "Burkina Faso": ["Africa"],
    Burundi: ["Africa"],
    Cambodia: ["Asia"],
    Cameroon: ["Africa"],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "Cape Verde": ["Africa"],
    "Central African Republic": ["Africa"],
    Chad: ["Africa"],
    Chile: ["South America"],
    China: ["Asia"],
    Colombia: ["South America"],
    Comoros: ["Africa"],
    Congo: ["Africa"],
    "Costa Rica": ["North America"],
    Croatia: ["Europe"],
    Cuba: ["North America"],
    Cyprus: ["Europe"],
    "Czech Republic": ["Europe"],
    Denmark: ["Europe"],
    Djibouti: ["Africa"],
    "Dominican Republic": ["North America"],
    Ecuador: ["South America"],
    Egypt: ["Africa"],
    "El Salvador": ["North America"],
    Estonia: ["Europe"],
    Ethiopia: ["Africa"],
    Finland: ["Europe"],
    France: ["Europe"],
    Germany: ["Europe"],
    Ghana: ["Africa"],
    Greece: ["Europe"],
    Guatemala: ["North America"],
    Haiti: ["North America"],
    Honduras: ["North America"],
    Hungary: ["Europe"],
    Iceland: ["Europe"],
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    Indonesia: ["Asia"],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    Iraq: ["Asia"],
    Ireland: ["Europe"],
    Israel: ["Asia"],
    Italy: ["Europe"],
    Jamaica: ["North America"],
    Japan: ["Asia"],
    Jordan: ["Asia"],
    Kazakhstan: ["Asia"],
    Kenya: ["Africa"],
    Kuwait: ["Asia"],
    Kyrgyzstan: ["Asia"],
    Laos: ["Asia"],
    Latvia: ["Europe"],
    Lebanon: ["Asia"],
    Lesotho: ["Africa"],
    Liberia: ["Africa"],
    Libya: ["Africa"],
    Lithuania: ["Europe"],
    Luxembourg: ["Europe"],
    Madagascar: ["Africa"],
    Malawi: ["Africa"],
    Malaysia: ["Asia"],
    Maldives: ["Asia"],
    Mali: ["Africa"],
    Malta: ["Europe"],
    Mexico: ["North America"],
    Moldova: ["Europe"],
    Mongolia: ["Asia"],
    Morocco: ["Africa"],
    Mozambique: ["Africa"],
    Myanmar: ["Asia"],
    Namibia: ["Africa"],
    Nepal: ["Asia"],
    Netherlands: ["Europe"],
    "New Zealand": ["Oceania"],
    Nicaragua: ["North America"],
    Niger: ["Africa"],
    Nigeria: ["Africa"],
    "North Korea": ["Asia"],
    Norway: ["Europe"],
    Oman: ["Asia"],
    Pakistan: ["Asia"],
    Panama: ["North America"],
    Paraguay: ["South America"],
    Peru: ["South America"],
    Philippines: ["Asia"],
    Poland: ["Europe"],
    Portugal: ["Europe"],
    Qatar: ["Asia"],
    Romania: ["Europe"],
    Russia: ["Europe"],
    Rwanda: ["Africa"],
    "Saudi Arabia": ["Asia"],
    Senegal: ["Africa"],
    Serbia: ["Europe"],
    Singapore: ["Asia"],
    Slovakia: ["Europe"],
    Slovenia: ["Europe"],
    Somalia: ["Africa"],
    "South Africa": ["Africa"],
    "South Korea": ["Asia"],
    Spain: ["Europe"],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Sudan: ["Africa"],
    Sweden: ["Europe"],
    Switzerland: ["Europe"],
    Syria: ["Asia"],
    Taiwan: ["Asia"],
    Tanzania: ["Africa"],
    Thailand: ["Asia"],
    Tunisia: ["Africa"],
    Turkey: ["Asia"],
    Uganda: ["Africa"],
    Ukraine: ["Europe"],
    "United Arab Emirates": ["Asia"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    "United States": ["North America"],
    Uruguay: ["South America"],
    Uzbekistan: ["Asia"],
    Venezuela: ["South America"],
    Vietnam: ["Asia"],
    Yemen: ["Asia"],
    Zambia: ["Africa"],
    Zimbabwe: ["Africa"],
  };

  const statusOptions = [
    { value: "approved", label: "Approved" },
    { value: "awaiting", label: "Awaiting" },
    { value: "suspended", label: "Suspended" },
    { value: "rejected", label: "Rejected" },
  ];

  const validateBasicDetails = () => {
    let valid = true;

    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      setFirstNameError("First name must contain only letters.");
      valid = false;
    } else if (firstName.trim().length < 3) {
      setFirstNameError("First name must be at least 3 letters long.");
      valid = false;
    } else {
      setFirstNameError("");
    }

    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      setLastNameError("Last name must contain only letters.");
      valid = false;
    } else if (lastName.trim().length < 3) {
      setLastNameError("Last name must be at least 3 letters long.");
      valid = false;
    } else {
      setLastNameError("");
    }

    if (!email.trim()) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      if (!emailError.includes("already registered")) {
        setEmailError("");
      }
    }

    const phoneErr = phoneErrorSuperAdmin(
      phoneNumber,
      getPhoneIso2(lastSelectedPhoneCountryRef.current),
    );
    if (phoneErr) {
      setPhoneErrorMessage(phoneErr);
      valid = false;
    } else if (
      phoneErrorMessage &&
      !phoneErrorMessage.includes("already registered")
    ) {
      setPhoneErrorMessage("");
    }

    return valid;
  };

  const validateAddressDetails = () => {
    let valid = true;

    if (!addressLine1.trim()) {
      setLine1Error("Address line 1 is required.");
      valid = false;
    } else {
      setLine1Error("");
    }

    if (!city.trim()) {
      setCityError("City is required.");
      valid = false;
    } else {
      setCityError("");
    }

    if (!postalCode.trim()) {
      setPostalCodeError("Postal code is required.");
      valid = false;
    } else if (!isValidPostalCode(postalCode, country)) {
      setPostalCodeError("Please enter a valid postal code.");
      valid = false;
    } else {
      setPostalCodeError("");
    }

    return valid;
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError("");

    try {
      const agentId = getAgentId();

      if (!agentId) {
        throw new Error("Agent ID not found. Cannot update profile.");
      }

      const updateData = {
        first_name: firstName,
        last_name: lastName,
        ...(email !== originalEmail && { email: email }),
        ...(phoneNumber !== originalPhone && { phone_number: phoneNumber }),
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_line3: addressLine3,
        city: city,
        postcode: postalCode,
        country_name: country,
        region: region,
      };

      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined || updateData[key] === null) {
          delete updateData[key];
        }
      });

      console.log("Updating agent with data:", updateData);

      const currentStatus = agent.current_status || agent.status;
      if (status && status.value !== currentStatus) {
        await advertisingAgentAPI.updateAgentStatus(agentId, {
          user_status: status.value,
          notes: note || `Status changed to ${status.value} by admin`,
        });
      }

      const updatedAgent = {
        ...agent,
        firstName: firstName,
        surname: lastName,
        first_name: firstName,
        last_name: lastName,
        email: email,
        mobile: phoneNumber,
        phone_number: phoneNumber,
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_line3: addressLine3,
        city: city,
        postcode: postalCode,
        region: region,
        country: country,
        address: {
          line1: addressLine1,
          line2: addressLine2,
          line3: addressLine3,
          city: city,
          postcode: postalCode,
          county: region,
          country: country,
        },
        current_status: status?.value || currentStatus,
        status: status?.value || currentStatus,
        status_note: note,
        notes: note || agent.notes,
        agent_type: agent.agent_type || application?.agent_type,
      };

      showSuccess("Agent profile updated successfully!");

      setTimeout(() => {
        navigate("/advertising-agent-details", {
          state: {
            agent: updatedAgent,
            application: application,
            message: "Agent profile updated successfully!",
          },
        });
      }, 1500);
    } catch (err) {
      console.error("Error updating agent:", err);

      if (err.response?.data?.errors?.email) {
        showError(
          "Email address is already taken. Please use a different email.",
        );
      } else if (err.response?.data?.errors?.phone_number) {
        showError(
          "Phone number is already registered. Please use a different phone number.",
        );
      } else if (err.response?.data?.message) {
        showError(err.response.data.message);
      } else {
        showError("Failed to update agent profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFirstName(agent.first_name || agent.firstName || "");
    setLastName(agent.last_name || agent.surname || "");
    setEmail(originalEmail);
    setPhoneNumber(originalPhone);
    setAddressLine1(agent.address_line1 || agent.address?.line1 || "");
    setAddressLine2(agent.address_line2 || agent.address?.line2 || "");
    setAddressLine3(agent.address_line3 || agent.address?.line3 || "");
    setCity(agent.city || agent.address?.city || "");
    setPostalCode(originalPostalCode);
    setRegion(agent.region || agent.address?.county || "");
    setCountry(agent.country || agent.address?.country || "");
    setNote(agent.status_note || agent.notes || agent.note || "");

    const currentStatus = agent.current_status || agent.status;
    if (currentStatus) {
      setStatus({
        value: currentStatus,
        label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1),
      });
    } else {
      setStatus(null);
    }

    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setPhoneErrorMessage("");
    setLine1Error("");
    setCityError("");
    setPostalCodeError("");
    setError("");

    setEmailAvailable(null);
    setEmailCheckMessage("");

    setSelectedAreas([]);
    setSelectedFocusCountries([]);
    setStep(1);

    showInfo("Changes cancelled");
  };

  const currentCountryOption = country
    ? { value: country, label: country }
    : null;
  const currentRegionOption = region ? { value: region, label: region } : null;

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        <div className="edit-header">
          <h2>Edit Profile</h2>
          <p>You can edit your details to update your profile</p>
        </div>

        <div className="breadcrumb">
          <Link to="/manage-advertising-agent" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-advertising-agent" className="breadcrumb-link">
            Manage Customer Advertising Agent
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Edit Profile</span>
        </div>

        {error && (
          <div
            className="error-message"
            style={{
              margin: "1rem 0",
              padding: "0.75rem",
              background: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
            }}
          >
            {error}
          </div>
        )}

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <button
                    type="button"
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    } clickable`}
                    onClick={() => handleStepClick(stepNum)}
                    disabled={isActive}
                    title={`Go to Step ${stepNum}: ${item.label}`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </button>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      First name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (firstNameError) setFirstNameError("");
                      }}
                      placeholder="Enter first name"
                     maxLength={255} />
                    <div className="error-container">
                      {firstNameError && (
                        <p className="error-text">{firstNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Last name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (lastNameError) setLastNameError("");
                      }}
                      placeholder="Enter last name"
                     maxLength={255} />
                    <div className="error-container">
                      {lastNameError && (
                        <p className="error-text">{lastNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmail(value);
                        setEmailAvailable(null);
                        setEmailCheckMessage("");
                        if (
                          emailError &&
                          !emailError.includes("already registered")
                        ) {
                          setEmailError("");
                        }
                      }}
                      placeholder="Enter email"
                      className={
                        email !== originalEmail
                          ? emailAvailable === false
                            ? "email-taken"
                            : emailAvailable === true
                              ? "email-available"
                              : ""
                          : ""
                      }
                     maxLength={255} />

                    {email !== originalEmail && (
                      <>
                        {emailChecking && (
                          <div className="email-status checking">
                            Checking email availability...
                          </div>
                        )}

                        {!emailChecking && emailCheckMessage && (
                          <div
                            className={`email-status ${emailAvailable === true ? "available" : emailAvailable === false ? "taken" : ""}`}
                          >
                            {emailCheckMessage}
                          </div>
                        )}
                      </>
                    )}

                    <div className="error-container">
                      {emailError && <p className="error-text">{emailError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="phone-input-container">
                      <PhoneInput
                        country={"gb"}
                        enableLongNumbers
                        value={phoneNumber}
                        onChange={(value, country) => {
                          lastSelectedPhoneCountryRef.current = country || null;
                          setPhoneNumber(clampAdminPhoneString(value));

                          if (
                            phoneErrorMessage &&
                            !phoneErrorMessage.includes("already registered")
                          ) {
                            setPhoneErrorMessage("");
                          }
                        }}
                        inputProps={{
                          name: "phone_number",
                          required: true,
                        }}
                        inputClass="phone-input"
                        containerClass="phone-container"
                      />
                    </div>

                    <div className="error-container">
                      {phoneErrorMessage && <p className="error-text">{phoneErrorMessage}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Address</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Address line 1 <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={addressLine1}
                      onChange={(e) => {
                        setAddressLine1(e.target.value);
                        if (line1Error) setLine1Error("");
                      }}
                      placeholder="Enter address line 1"
                     maxLength={255} />
                    <div className="error-container">
                      {line1Error && <p className="error-text">{line1Error}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address line 2</label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Enter address line 2"
                     maxLength={255} />
                    <div className="error-container" />
                  </div>

                  <div className="form-group full-width">
                    <label>Address line 3</label>
                    <input
                      type="text"
                      value={addressLine3}
                      onChange={(e) => setAddressLine3(e.target.value)}
                      placeholder="Enter address line 3"
                     maxLength={255} />
                    <div className="error-container" />
                  </div>

                  <div className="form-group">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (cityError) setCityError("");
                      }}
                      placeholder="Enter city"
                     maxLength={255} />
                    <div className="error-container">
                      {cityError && <p className="error-text">{cityError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Region <span className="required">*</span>
                    </label>
                    <Select
                      options={
                        country
                          ? regionOptions[country]?.map((r) => ({
                              value: r,
                              label: r,
                            })) || []
                          : []
                      }
                      value={currentRegionOption}
                      onChange={(option) => setRegion(option.value)}
                      placeholder="Select or type region"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>

                  <div className="form-group">
                    <label>
                      Post Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => {
                        setPostalCode(e.target.value);
                        if (postalCodeError) setPostalCodeError("");
                      }}
                      placeholder="Enter postal code"
                     maxLength={20} />
                    <div className="error-container">
                      {postalCodeError && (
                        <p className="error-text">{postalCodeError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Country <span className="required">*</span>
                    </label>
                    <Select
                      options={Object.keys(regionOptions).map((c) => ({
                        value: c,
                        label: c,
                      }))}
                      value={currentCountryOption}
                      onChange={(option) => {
                        setCountry(option.value);
                        setRegion("");
                        const regions = regionOptions[option.value];
                        if (regions && regions.length > 0) {
                          setRegion(regions[0]);
                        }
                      }}
                      placeholder="Select or type country"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3>Status</h3>
                <hr />
                <div className="form-grid center-step">
                  <div className="form-group">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Select Status"
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused
                            ? "0 0 0px rgba(21, 21, 22, 0.4)"
                            : "none",
                          "&:hover": "none",
                          fontSize: "15px",
                          color: "black",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="note-textarea"
                      rows="8"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter note here"
                     maxLength={65535} />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && isSeniorAgent && (
              <div>
                <h3>Admin Areas</h3>
                <hr />

                {/* Currently Assigned Areas with Remove Option */}
                {assignedAreas.length > 0 && (
                  <div
                    style={{
                      marginBottom: "20px",
                      background: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <h4>Currently Assigned Areas ({assignedAreas.length})</h4>
                      <div>
                        {selectedAreas.length > 0 && (
                          <button
                            onClick={handleBulkRemoveSelected}
                            style={{
                              background: "#dc3545",
                              color: "white",
                              border: "none",
                              padding: "5px 10px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                              marginRight: "10px",
                            }}
                          >
                            Remove Selected ({selectedAreas.length})
                          </button>
                        )}
                        <button
                          onClick={handleRemoveAllAreas}
                          style={{
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            padding: "5px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Remove All
                        </button>
                      </div>
                    </div>

                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {assignedAreas.map((area) => (
                        <div
                          key={area.admin_area_id}
                          style={{
                            padding: "8px",
                            margin: "5px 0",
                            background: "white",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAreas.some(
                                (a) => a.admin_area_id === area.admin_area_id,
                              )}
                              onChange={() => toggleAreaSelection(area)}
                              style={{ cursor: "pointer" }}
                            />
                            <div>
                              <strong>{area.admin_name}</strong>
                              {area.county && (
                                <span
                                  style={{ color: "#666", marginLeft: "8px" }}
                                >
                                  ({area.county.county_name})
                                </span>
                              )}
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#888",
                                  marginTop: "4px",
                                }}
                              >
                                Towns: {area.towns_count} | Assigned:{" "}
                                {new Date(
                                  area.assigned_at,
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveArea(area.admin_area_id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#dc3545",
                              cursor: "pointer",
                              fontSize: "18px",
                              padding: "0 8px",
                            }}
                            title="Remove this area"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Filter */}
                <div
                  style={{
                    marginBottom: "20px",
                    background: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "6px",
                  }}
                >
                  <h4>Filter Available Areas</h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: "10px",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "14px",
                        }}
                      >
                        Search
                      </label>
                      <input
                        type="text"
                        value={areaFilters.search}
                        onChange={(e) =>
                          setAreaFilters({
                            ...areaFilters,
                            search: e.target.value,
                          })
                        }
                        placeholder="Search by area name..."
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                        }}
                       maxLength={20} />
                    </div>
                    <button
                      onClick={fetchAvailableAreas}
                      style={{
                        padding: "8px 16px",
                        background: "rgba(0, 0, 160, 1)",
                        color: "white",
                        border: "none",
                        borderRadius: "17.6px",
                        cursor: "pointer",
                        height: "38px",
                      }}
                    >
                      Search
                    </button>
                  </div>
                </div>

                {/* Available Areas */}
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <h4>Available Areas ({availableAreas.length})</h4>
                    <div>
                      <button
                        onClick={selectAllVisible}
                        style={{
                          marginRight: "10px",
                          background: "none",
                          border: "none",
                          color: "rgba(0, 0, 160, 1)",
                          cursor: "pointer",
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={clearSelections}
                        style={{
                          background: "none",
                          border: "none",
                          color: "rgba(0, 0, 160, 1)",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {loadingAreas ? (
                    <p>Loading...</p>
                  ) : availableAreas.length === 0 ? (
                    <p>No admin areas found</p>
                  ) : (
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "10px",
                      }}
                    >
                      {availableAreas.map((area) => (
                        <div
                          key={area.admin_area_id}
                          style={{
                            padding: "10px",
                            margin: "5px 0",
                            background: area.is_assigned
                              ? "#f5f5f5"
                              : selectedAreas.some(
                                    (a) =>
                                      a.admin_area_id === area.admin_area_id,
                                  )
                                ? "#e3f2fd"
                                : "white",
                            borderRadius: "4px",
                            border: `1px solid ${
                              area.is_assigned
                                ? "#ccc"
                                : selectedAreas.some(
                                      (a) =>
                                        a.admin_area_id === area.admin_area_id,
                                    )
                                  ? "rgba(0, 0, 160, 1)"
                                  : "#e0e0e0"
                            }`,
                            cursor: area.is_assigned
                              ? "not-allowed"
                              : "pointer",
                            opacity: area.is_assigned ? 0.7 : 1,
                          }}
                          onClick={() =>
                            !area.is_assigned && toggleAreaSelection(area)
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <strong>{area.admin_name}</strong>
                              {area.county && (
                                <span
                                  style={{ color: "#666", marginLeft: "8px" }}
                                >
                                  ({area.county.county_name})
                                </span>
                              )}
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#888",
                                  marginTop: "4px",
                                }}
                              >
                                Towns: {area.towns_count}
                              </div>
                            </div>
                            {area.is_assigned && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  background: "#4caf50",
                                  color: "white",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                }}
                              >
                                Already Assigned
                              </span>
                            )}
                            {!area.is_assigned &&
                              selectedAreas.some(
                                (a) => a.admin_area_id === area.admin_area_id,
                              ) && (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    background: "rgba(0, 0, 160, 1)",
                                    color: "white",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                  }}
                                >
                                  Selected
                                </span>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Options */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                    />
                    <span>
                      Replace all existing assignments (removes unselected
                      areas)
                    </span>
                  </label>
                </div>

                {/* Selected Count */}
                {selectedAreas.length > 0 && (
                  <div
                    style={{
                      marginBottom: "10px",
                      color: "rgba(0, 0, 160, 1)",
                    }}
                  >
                    {selectedAreas.length} area(s) selected
                  </div>
                )}
              </div>
            )}

            {focusCountriesStep != null &&
              step === focusCountriesStep &&
              isAdvertisingAgentForFocus && (
              <div>
                <h3>Focus Countries</h3>
                <hr />

                {/* Focus Country Statistics Summary */}
                {assignedFocusCountries.length > 0 && (
                  <div
                    style={{
                      marginBottom: "20px",
                      background: "#e8f4fd",
                      padding: "15px",
                      borderRadius: "6px",
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <strong>Total:</strong> {focusCountryStats.total}
                    </div>
                    <div style={{ color: "#1976d2" }}>
                      <strong>Primary:</strong> {focusCountryStats.primary}
                    </div>
                    <div style={{ color: "#2e7d32" }}>
                      <strong>Secondary:</strong> {focusCountryStats.secondary}
                    </div>
                    <div style={{ color: "#ed6c02" }}>
                      <strong>Target:</strong> {focusCountryStats.target}
                    </div>
                  </div>
                )}

                {/* Currently Assigned Focus Countries with Remove Option */}
                {assignedFocusCountries.length > 0 && (
                  <div
                    style={{
                      marginBottom: "20px",
                      background: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <h4>
                        Currently Assigned Focus Countries (
                        {assignedFocusCountries.length})
                      </h4>
                      <div>
                        {selectedFocusCountries.length > 0 && (
                          <button
                            onClick={handleBulkRemoveSelectedFocus}
                            style={{
                              background: "#dc3545",
                              color: "white",
                              border: "none",
                              padding: "5px 10px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                              marginRight: "10px",
                            }}
                          >
                            Remove Selected ({selectedFocusCountries.length})
                          </button>
                        )}
                        <button
                          onClick={handleRemoveAllFocusCountries}
                          style={{
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            padding: "5px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Remove All
                        </button>
                      </div>
                    </div>

                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      {assignedFocusCountries.map((country) => (
                        <div
                          key={country.country_id}
                          style={{
                            padding: "12px",
                            margin: "5px 0",
                            background: "white",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px",
                                flex: 1,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedFocusCountries.some(
                                  (c) => c.country_id === country.country_id,
                                )}
                                onChange={() =>
                                  toggleFocusCountrySelection(country)
                                }
                                style={{ cursor: "pointer", marginTop: "2px" }}
                              />
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <strong style={{ fontSize: "16px" }}>
                                    {country.country_name}
                                  </strong>
                                  <span
                                    style={{
                                      background:
                                        country.focus_type === "primary"
                                          ? "#1976d2"
                                          : country.focus_type === "secondary"
                                            ? "#2e7d32"
                                            : "#ed6c02",
                                      color: "white",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {country.focus_type_label}
                                  </span>
                                </div>
                                {country.notes && (
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      color: "#666",
                                      marginTop: "4px",
                                      background: "#f5f5f5",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <em>{country.notes}</em>
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#888",
                                    marginTop: "6px",
                                    display: "flex",
                                    gap: "15px",
                                  }}
                                >
                                  <span>
                                    Assigned:{" "}
                                    {new Date(
                                      country.assigned_at,
                                    ).toLocaleDateString()}
                                  </span>
                                  <span>
                                    Updated:{" "}
                                    {new Date(
                                      country.updated_at,
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <select
                                value={country.focus_type}
                                onChange={(e) =>
                                  handleUpdateFocusType(
                                    country.country_id,
                                    e.target.value,
                                  )
                                }
                                style={{
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                  fontSize: "12px",
                                  marginRight: "8px",
                                }}
                              >
                                {focusTypeOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() =>
                                  handleRemoveFocusCountry(country.country_id)
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#dc3545",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  padding: "0 8px",
                                }}
                                title="Remove this focus country"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Filter Available Focus Countries */}
                <div
                  style={{
                    marginBottom: "20px",
                    background: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "6px",
                  }}
                >
                  <h4>Filter Available Countries</h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: "10px",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "14px",
                        }}
                      >
                        Search
                      </label>
                      <input
                        type="text"
                        value={focusCountryFilters.search}
                        onChange={(e) =>
                          setFocusCountryFilters({
                            ...focusCountryFilters,
                            search: e.target.value,
                          })
                        }
                        placeholder="Search by country name..."
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                        }}
                       maxLength={20} />
                    </div>
                    <button
                      onClick={fetchAvailableFocusCountries}
                      style={{
                        padding: "8px 16px",
                        background: "rgba(0, 0, 160, 1)",
                        color: "white",
                        border: "none",
                        borderRadius: "17.6px",
                        cursor: "pointer",
                        height: "38px",
                      }}
                    >
                      Search
                    </button>
                  </div>
                </div>

                {/* Available Focus Countries - Deduplicated by country name */}
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <h4>
                      Available Countries ({availableFocusCountries.length})
                    </h4>
                    <div>
                      <button
                        onClick={selectAllVisibleFocus}
                        style={{
                          marginRight: "10px",
                          background: "none",
                          border: "none",
                          color: "rgba(0, 0, 160, 1)",
                          cursor: "pointer",
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={clearFocusSelections}
                        style={{
                          background: "none",
                          border: "none",
                          color: "rgba(0, 0, 160, 1)",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {loadingFocusCountries ? (
                    <p>Loading...</p>
                  ) : availableFocusCountries.length === 0 ? (
                    <p>No available countries found</p>
                  ) : (
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "10px",
                      }}
                    >
                      {availableFocusCountries.map((country) => (
                        <div
                          key={country.id}
                          style={{
                            padding: "10px",
                            margin: "5px 0",
                            background: selectedFocusCountries.some(
                              (c) => c.country_id === country.id,
                            )
                              ? "#e3f2fd"
                              : "white",
                            borderRadius: "4px",
                            border: `1px solid ${
                              selectedFocusCountries.some(
                                (c) => c.country_id === country.id,
                              )
                                ? "rgba(0, 0, 160, 1)"
                                : "#e0e0e0"
                            }`,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            // Convert country object to expected format
                            const countryObj = {
                              country_id: country.id,
                              country_name: country.name,
                            };
                            toggleFocusCountrySelection(countryObj);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <strong>{country.name}</strong>
                            </div>
                            {selectedFocusCountries.some(
                              (c) => c.country_id === country.id,
                            ) && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  background: "rgba(0, 0, 160, 1)",
                                  color: "white",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                }}
                              >
                                Selected
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Options */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={replaceExistingFocus}
                      onChange={(e) =>
                        setReplaceExistingFocus(e.target.checked)
                      }
                    />
                    <span>
                      Replace all existing focus countries (removes unselected
                      countries)
                    </span>
                  </label>
                </div>

                {/* Selected Count */}
                {selectedFocusCountries.length > 0 && (
                  <div
                    style={{
                      marginBottom: "10px",
                      color: "rgba(0, 0, 160, 1)",
                    }}
                  >
                    {selectedFocusCountries.length} country(s) selected
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {(step === 3 || (isMiniAdvertisingAgent && step === 4)) && (
              <button
                className="btn prev"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            )}

            {step > 1 && (
              <button
                className="btn prev"
                onClick={prevStep}
                disabled={loading}
              >
                Previous
              </button>
            )}

            {step < maxWizardStep && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={loading || emailChecking}
                style={{
                  opacity: !loading && !emailChecking ? 1 : 0.6,
                  cursor: !loading && !emailChecking ? "pointer" : "not-allowed",
                }}
              >
                {emailChecking ? "Checking email..." : "Next"}
              </button>
            )}

            {step === 3 && !isSeniorAgent && !isMiniAdvertisingAgent && (
              <button
                className="btn next"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            )}

            {focusCountriesStep != null &&
              step === focusCountriesStep &&
              isAdvertisingAgentForFocus && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={loading}
              >
                {loading ? "Assigning & Updating..." : "Assign & Update"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Material-UI Snackbar for alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default AdvertisingAgentEdit;